import { oc } from "@orpc/contract";
import { z } from "zod";
import { ProcSavedReportDocumentSchema } from "@erp/shared-types";

/**
 * Saved query-builder reports.
 *
 * Per-user CRUD over the persisted {@link ProcSavedReportDocumentSchema} blob
 * (query IR + visual report layout), stored in `core_saved_reports`. The `list`
 * response embeds the full document so the "Open" dialog can load a report
 * without a second round-trip.
 */

export const ProcSavedReportSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The report's owner (always the requesting user — reports are per-user). */
  createdBy: z.object({ id: z.string().uuid(), name: z.string() }),
  report: ProcSavedReportDocumentSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CreateProcSavedReportInputSchema = z.object({
  name: z.string().min(1).max(255),
  report: ProcSavedReportDocumentSchema,
});

export const UpdateProcSavedReportInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  report: ProcSavedReportDocumentSchema.optional(),
});

export const DeleteProcSavedReportInputSchema = z.object({
  id: z.string().uuid(),
});

export const procSavedReportsContracts = {
  create: oc
    .input(CreateProcSavedReportInputSchema)
    .output(ProcSavedReportSchema)
    .route({
      method: "POST",
      summary: "Save a procurement query report",
      description:
        "Saves a new named query-builder report (query plus visual layout) owned by the current user.",
    }),
  list: oc.output(z.object({ reports: z.array(ProcSavedReportSchema) })).route({
    method: "GET",
    summary: "List my saved procurement reports",
    description:
      "Returns the current user's saved query-builder reports, each with its full query and layout so it can be opened directly.",
  }),
  update: oc
    .input(UpdateProcSavedReportInputSchema)
    .output(ProcSavedReportSchema)
    .route({
      method: "POST",
      summary: "Update a saved procurement report",
      description:
        "Renames a saved query-builder report and/or replaces its query and layout. Only the current user's own reports.",
    }),
  delete: oc
    .input(DeleteProcSavedReportInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "POST",
      summary: "Delete a saved procurement report",
      description:
        "Deletes one of the current user's saved query-builder reports by id.",
    }),
} as const;

export type ProcSavedReport = z.infer<typeof ProcSavedReportSchema>;
export type CreateProcSavedReportInput = z.infer<
  typeof CreateProcSavedReportInputSchema
>;
export type UpdateProcSavedReportInput = z.infer<
  typeof UpdateProcSavedReportInputSchema
>;
export type DeleteProcSavedReportInput = z.infer<
  typeof DeleteProcSavedReportInputSchema
>;
